import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import type { User } from '@core/account-types';
import { AdminsAvatarsComponent, AvatarUser } from '@core/account-ui';
import type { QuickEvaluationContext } from '@core/analytics-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import type { InterviewGroupBy } from '@core/interview-results-types';
import { AppNavigatorService } from '@core/navigation-legacy';
import type { TOfferKind } from '@core/offers-types';
import { OfferSelectOption, OfferStatusIndicatorComponent } from '@core/offers-ui';
import type { Stage } from '@core/stages-types';
import { injectUnbiasedHiringTranslations } from '@core/unbiased-hiring-legacy';
import {
  BoxComponent,
  ButtonComponent,
  ConnectedOverlayDirective,
  GroupComponent,
  IconComponent,
  OverlayOriginDirective,
  TruncateComponent,
} from '@core/user-interface-legacy';
import type { AddEvaluationSubmit } from '@recruitee/evaluations-types';

import { AddEvaluationDropdownComponent } from '../dropdowns/add-evaluation-dropdown/add-evaluation-dropdown.component';

@Component({
  selector: 'rt-evaluations-hidden-infobox',
  templateUrl: './hidden-evaluations-infobox.component.html',
  styleUrls: ['./hidden-evaluations-infobox.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    IconComponent,
    OfferStatusIndicatorComponent,
    TruncateComponent,
    AdminsAvatarsComponent,
    ButtonComponent,
    GroupComponent,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    AddEvaluationDropdownComponent,
    TranslatePipe,
  ],
})
export class HiddenEvaluationsInfoboxComponent {
  private readonly appNavigator = inject(AppNavigatorService);

  public readonly translations = injectUnbiasedHiringTranslations();

  public readonly admins = input([], {
    transform: (value: AvatarUser[] | undefined) => value || [],
  });
  public readonly guests = input([], {
    transform: (value: AvatarUser[] | undefined) => value || [],
  });
  public readonly stage = input<Stage>();
  public readonly isJobNameVisible = input<boolean>(undefined!);
  public readonly isButtonsVisible = input<boolean>(undefined!);
  public readonly groupBy = input<InterviewGroupBy>();
  public readonly count = input<number>(undefined!);
  public readonly short = input<boolean | undefined>(false);
  public readonly offers = input<OfferSelectOption[]>();
  public readonly candidateId = input<number>();
  public readonly marginBottom = input<boolean>();
  public readonly canReveal = input<boolean>(false);
  public readonly hasAccessToJob = input<boolean>();
  public readonly admin = input<User>();
  public readonly offer = input<{
    title: string;
    status: string;
    kind: TOfferKind;
    id: number;
  } | null>(null);

  public members = computed(() => [...this.admins(), ...this.guests()]);
  public quickEvaluationClick = output<{
    offerId: number;
    context: QuickEvaluationContext;
  }>();
  public counterClick = output<void>();
  public revealClick = output<void>();

  public renderAdminTooltip(adminOrGuest: AvatarUser) {
    return adminOrGuest.name || `${adminOrGuest.firstName} ${adminOrGuest.lastName}`;
  }

  public onAddEvaluation(data: AddEvaluationSubmit): void {
    this.appNavigator.openCandidateDialog(this.candidateId()!, {
      openInterview: true,
      interviewOfferId: data.offerId,
      interviewTalentPoolId: data.talentPoolId,
      interviewTemplateId: data.templateId,
      fromHiddenEvaluations: true,
    });
  }

  public onAddQuickEvaluation(): void {
    this.appNavigator.openCandidateDialog(this.candidateId()!, {
      openInterview: true,
      quickEvaluation: true,
      fromHiddenEvaluations: true,
    });
  }

  public get correctOffer() {
    return <OfferSelectOption & { id: number }>(
      this.offers()?.find(offer => this.offer()?.id === offer.id)!
    );
  }
}
